import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { FaArrowRight, FaCheck, FaAngleRight } from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import {
  getServiceCategoryBySlug,
  categoryMeta,
  allServices,
} from '../config/servicesMenuConfig';
import { useRegion } from '../context/RegionContext';
import './ServiceDetailPage.css';

const ServiceCategoryPage = () => {
  const { slug } = useParams();
  const { region } = useRegion();
  const cat = getServiceCategoryBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (cat) {
      document.title = cat.seoTitle || `${cat.category} | Proven InfoSec`;
      const metaDesc = document.querySelector('meta[name="description"]');
      const content = cat.seoDesc || cat.intro;
      if (metaDesc) {
        metaDesc.setAttribute('content', content);
      } else {
        const metaEl = document.createElement('meta');
        metaEl.name = 'description';
        metaEl.content = content;
        document.head.appendChild(metaEl);
      }
    }
  }, [slug, cat]);

  // Structured data: BreadcrumbList + an ItemList of the category's services.
  useEffect(() => {
    if (!cat) return undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const services = allServices.filter((s) => s.category === cat.category && !s.hidden);
    const graph = [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${origin}/services` },
          { '@type': 'ListItem', position: 3, name: cat.category, item: `${origin}/services/category/${cat.slug}` },
        ],
      },
      {
        '@type': 'ItemList',
        name: `${cat.category} services`,
        itemListElement: services.map((s, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: s.label,
          url: `${origin}${s.href}`,
        })),
      },
    ];
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'svc-cat-jsonld';
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
    document.getElementById('svc-cat-jsonld')?.remove();
    document.head.appendChild(script);
    return () => document.getElementById('svc-cat-jsonld')?.remove();
  }, [slug, cat]);

  if (!cat) {
    return <Navigate to="/services" replace />;
  }

  const meta = categoryMeta[cat.category] || {};
  const features = meta.features || [];
  const processSteps = meta.process || [];
  const whySection = meta.why || null;
  const stats = meta.stats || [];
  // Services in this category, respecting region gating (matches the menu).
  const services = allServices.filter(
    (s) => s.category === cat.category && !s.hidden && (!s.region || s.region === region)
  );

  return (
    <div className="svc-page">
      <PageNavbar />

      {/* ---------- Hero ---------- */}
      <section className="svc-hero">
        <div className="svc-hero-glow" />
        <div className="svc-container svc-hero-inner">
          <motion.div className="svc-hero-text" {...getRevealProps('fadeUp')}>
            <nav className="svc-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaAngleRight />
              <Link to="/services">Services</Link>
              <FaAngleRight />
              <span>{cat.category}</span>
            </nav>

            <span className="svc-eyebrow">Service Category</span>
            <h1 className="svc-hero-title">{cat.category}</h1>
            <p className="svc-hero-desc">{cat.intro}</p>

            <div className="svc-hero-actions">
              <Link to="/contact" className="svc-btn svc-btn-primary">
                Get In Touch <FaArrowRight />
              </Link>
              <Link to="/services" className="svc-btn svc-btn-ghost">
                All Services
              </Link>
            </div>
          </motion.div>

          {(stats.length > 0 || features.length > 0) && (
            <motion.aside className="svc-hero-card" {...getRevealProps('slideRight', 0.12)}>
              <span className="svc-card-tag">At a glance</span>
              <div className="svc-stats">
                {stats.map((s) => (
                  <div className="svc-stat" key={s.label}>
                    <div className="svc-stat-value">{s.value}</div>
                    <div className="svc-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
              <ul className="svc-card-list">
                {features.map((feat) => (
                  <li key={feat.title}>
                    <FaCheck /> {feat.title}
                  </li>
                ))}
              </ul>
            </motion.aside>
          )}
        </div>
      </section>

      {/* ---------- Services in this category ---------- */}
      <section className="svc-section">
        <div className="svc-container">
          <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
            <span className="svc-section-tag">Services</span>
            <h2 className="svc-section-title">Explore our {cat.category} services</h2>
            <p className="svc-section-sub">
              Focused, expert-led services built around measurable security outcomes — choose the
              engagement that fits your needs.
            </p>
          </motion.header>

          <motion.div
            className="svc-cat-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={{
              ...staggerContainer,
              visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
            }}
          >
            {services.map((s) => (
              <motion.div key={s.slug} variants={staggerItem}>
                <Link to={s.href} className="svc-feature-card svc-cat-card">
                  <h3 className="svc-feature-title">{s.label}</h3>
                  <p className="svc-feature-desc">{s.description}</p>
                  <span className="svc-cat-card-link">
                    Learn more <FaArrowRight />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---------- Our approach (features) ---------- */}
      {processSteps.length > 0 && (
        <section className="svc-section svc-section-alt">
          <div className="svc-container">
            <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
              <span className="svc-section-tag">How We Work</span>
              <h2 className="svc-section-title">A clear, proven process</h2>
              <p className="svc-section-sub">
                A repeatable approach that keeps every engagement transparent, on-track, and
                outcome-driven.
              </p>
            </motion.header>

            <motion.ol
              className="svc-process"
              initial="hidden"
              whileInView="visible"
              viewport={defaultViewport}
              variants={{
                ...staggerContainer,
                visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
              }}
            >
              {processSteps.map((m) => (
                <motion.li className="svc-step" key={m.step} variants={staggerItem}>
                  <div className="svc-step-num">{m.step}</div>
                  <h3 className="svc-step-name">{m.name}</h3>
                  <p className="svc-step-desc">{m.desc}</p>
                </motion.li>
              ))}
            </motion.ol>
          </div>
        </section>
      )}

      {/* ---------- Why it matters ---------- */}
      {whySection && (
        <section className="svc-section">
          <div className="svc-container svc-why-grid">
            <motion.div className="svc-why-text" {...getRevealProps('slideLeft')}>
              <span className="svc-section-tag">Why It Matters</span>
              <h2 className="svc-section-title">{whySection.title}</h2>
              <p className="svc-why-body">{whySection.body}</p>
            </motion.div>

            <motion.ul className="svc-why-list" {...getRevealProps('slideRight', 0.12)}>
              {whySection.bullets.map((b) => (
                <li key={b}>
                  <span className="svc-why-check"><FaCheck /></span>
                  {b}
                </li>
              ))}
            </motion.ul>
          </div>
        </section>
      )}

      {/* ---------- CTA ---------- */}
      <section className="svc-cta">
        <div className="svc-container">
          <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
            <h2 className="svc-cta-title">Ready to strengthen your {cat.category.toLowerCase()}?</h2>
            <p className="svc-cta-desc">
              Talk to one of our certified security professionals about a tailored engagement — we
              respond to every inquiry within one business day.
            </p>
            <div className="svc-cta-actions">
              <Link to="/contact" className="svc-btn svc-btn-primary">
                Book Consultation <FaArrowRight />
              </Link>
              <Link to="/services" className="svc-btn svc-btn-ghost-light">
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceCategoryPage;
